import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { UserManager } from "oidc-client";
import { mapState } from "pinia";
import { useApp } from "../../useApp";
import moment from "moment";

import config from "../../../../common/config";
import { formatName } from "../../../../common/format-name";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import { ApplicationEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../../enums/enums";
import AuthService from "../../../../services/auth.service";
import { AgencyService } from "../../../../services/agency.service";
import { AnalyticsCategory, AnalyticsService } from "../../../../services/analytics.service";

export default defineComponent({
    props: {
        areRolesLoaded: { type: Boolean, default: false }
    },

    data(vm) {
        return {
            _analyticsServices: null as AnalyticsService,
            _agencyService: null,
            _user: null,
            _routeProcessSubscription: null,
            _userManager: null,
            _authService: null,
            _permissionManager: null,
            hasCertificationAccess: false,
            displayRoutes: [],
            defaultRoute: config.defaultRoute,
            userName: "",
            certificationUrl: `${config.certificationUrl}/auth/axxess_sso/login.php`,
            reportAllowedUsers: [
                "mari.cuellar@exclusivehomehealth.net",
                "alisa@sunsethospiceaz.com",
                "lsims@1047homehealth.com",
                "lbrowder9@gmail.com",
                "jbrown@firstlighthomecare.com",
                "vbansal@axxess.com",
                "arirodriguez@axxess.com",
                "tpn3k@msn.com",
                "rsmith@axxess.com",
                "aawoniyi@axxess.com",
                "tross@axxess.com",
                "tnguyen@axxess.com",
                "kzamora@axxess.com",
                "rreddy@axxess.com",
                "mgrace2329@yahoo.com"
            ],
            certificationReportsUrl:
                "https://app.powerbi.com/Redirect?action=OpenApp&appId=3f76f432-6471-4742-9f3e-a2fd44f9d9bc&ctid=7093a932-58c9-4171-bd7b-906981c3393b&domain_hint=axxessweb.com&noSignUpCheck=1",
            isLoggedIn: false,
            dialogRef: null,
            simiTreeHHUrl: `${config.simiTreeUrl}/map-hha900/select-market`,
            simiTreeHOSUrl: `${config.simiTreeUrl}/map-hospice910/select-market`,
            simitreeHHPermission: false as boolean,
            simitreeHOSPermission: false as boolean,
            isLoading: false,
            listShow: true as boolean,
            canShowPremium: true as boolean,
            showBellIcon: false as boolean,
            expireAfter: null,
            trainingListShow: true
        };
    },

    created() {
        this._authService = Container.instance.get(AuthService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._agencyService = Container.instance.get(AgencyService);
        this._userManager = Container.instance.get(UserManager);
        this._analyticsServices = Container.instance.get(AnalyticsService);
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    watch: {
        areRolesLoaded() {
            this.areRolesLoadedChanged();
        },
        organizations() {
            this.areRolesLoadedChanged();
        }
    },

    computed: {
        ...mapState(useApp, ["organizations"]),
        canViewReports() {
            return this.reportAllowedUsers.some(
                (email) => email.trim().toLowerCase() === this._user?.profile.email?.trim().toLowerCase()
            );
        }
    },

    mounted() {
        this.loadUser();
        this.getCertificationAccess();
        this.areRolesLoadedChanged();
        this.simitreePermissions();
        const firstDateOfMonth = this.getFirstWednesdayOfCurrentMonth();
        let today = moment();
        let isMonthChanged = moment(this.expireAfter).isBefore(today);
        if (isMonthChanged) {
            localStorage.removeItem("navigated-to-certification");
        }
        if (
            today.isBetween(firstDateOfMonth, this.expireAfter, "day", "[]") &&
            !localStorage.getItem("navigated-to-certification")
        ) {
            this.showBellIcon = true;
        }
    },

    methods: {
        getFirstWednesdayOfCurrentMonth() {
            let date = moment().startOf("month");
            while (date.day() !== 3) {
                date.add(1, "day");
            }
            this.expireAfter = moment(date).add(13, "days");
            return date;
        },
        areRolesLoadedChanged() {
            const isClinician = this._permissionManager.checkPermission(
                ParentPermissionEnum.clinician,
                PermissionActionEnum.canView
            );
            const hasAxxesCentralAccess = this.organizations?.length > 0;
            const routes = this.getAllActualRoutes();
            // Filtering routes to display tabs based on roles
            this.displayRoutes = routes?.filter((route: any) => {
                if (!!route.meta?.showAxxessPlanner) {
                    return isClinician;
                } else if (!!route.meta?.showAxxessCentral) {
                    return hasAxxesCentralAccess;
                } else if (!!route.meta?.showOther) {
                    return true;
                } else {
                    return false;
                }
            });
        },

        async simitreePermissions() {
            this.simitreeHHPermission = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.simitree,
                PermissionActionEnum.canView,
                ApplicationEnum.AgencyCore
            );

            this.simitreeHOSPermission = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.simitree,
                PermissionActionEnum.canView,
                ApplicationEnum.AxxessHospiceFE
            );
        },

        isActivePath(path: string) {
            return this.$route.matched?.some((route) => route.path == path);
        },

        async getCertificationAccess() {
            try {
                this.hasCertificationAccess = await this._agencyService.getHasCertificationAccess();
            } catch (e) {
                console.error(e);
            }
        },

        getAllActualRoutes() {
            return this.$router.options.routes?.filter((route: any) => route.meta?.nav) || [];
        },

        async loadUser() {
            this._user = await this._userManager.getUser();
            if (this._user) {
                this.isLoggedIn = await this._authService.isLoggedIn();
                this.userName = formatName(this._user.profile.given_name, this._user.profile.family_name, "FN LN");
            }
        },

        async goToRoute(path: string) {
            if (path == "/axxess-central") {
                this.$router.push(`/axxess-central/organizationLoading/?&name=My dashboard&fromPlanner=true`);
            } else {
                this.$router.push(path);
            }
        },
        toggleListShow() {
            if (!this.canShowPremium) return;
            this.listShow = !this.listShow;
        },
        redirectToCertification() {
            localStorage.setItem("navigated-to-certification", moment().toString());
            window.open(this.certificationUrl);
            this.showBellIcon = false;
            this._analyticsServices.logEvent({
                category: AnalyticsCategory.Certifications,
                action: "Training and Certifications"
            });
        },
        toggleTrainingListShow() {
            this.trainingListShow = !this.trainingListShow;
        }
    },

    unmounted() {
        this._routeProcessSubscription?.dispose();
    }
});
